import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import filesize from 'filesize';
import { List } from 'lucide-react';
import PropTypes from 'prop-types';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';
import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/shadcn/radio-group';
import { Textarea } from '@/shadcn/textarea';

import {
  handleUploadFileOnError,
  uploadConfig,
  uploadFile
} from '@/client/media';
import { useBeforeLeavingAlert } from '@/components/beforeLeavingAlert';
import { notify } from '@/components/toast';
import { DropZone } from '@/components/ui/dropZone/dropZone';
import Table from '@/components/ui/table';
import {
  DATA_PARSING_STATUS_AGGREGATING,
  DATA_PARSING_STATUS_PARSING
} from '@/constant/dataParsing';
import { formatNumber } from '@/utils';
import { runAggregation } from '@/utils/aggregation/aggregation.runner';
import { runCsvParser } from '@/utils/csv/csv.runner';
import { readXls, runXlsParser, sheetToJson } from '@/utils/xls';
import { runXMLParser } from '@/utils/xml/xmlParser';
import {
  buildTableConfigFromColumnNames,
  buildTableConfigFromMapping,
  importTableCommonConfig
} from './utils';

const emptyTableData = [];

export const DataImporter = memo(
  ({
    config,
    onSubmit = () => {},
    submitLabel = 'Submit Data',
    submitDisabled = false,
    onCancel = () => {},
    cancelLabel = 'Cancel',
    cachedUpdatesKey = null,
    traceInfoViewer = null
  }) => {
    const { setAskBeforeLeaving, setLeavingCallback } = useBeforeLeavingAlert();

    // data importer state
    const [importState, setImportState] = useState({});
    const [error, setError] = useState(null);
    const [importedData, setImportedData] = useState(null);
    const [dropZoneActive, setDropZoneActive] = useState(false);
    const [sheets, setSheets] = useState([]);
    const [workbook, setWorkbook] = useState({});
    const [showSheetsModal, setShowSheetsModal] = useState(false);
    const [fileData, setFile] = useState('');
    const [selectedSheet, setSelectedSheet] = useState('');

    // comments state
    const [commentsInputFocused, setCommentsInputFocused] = useState(false);
    const [comments, setComments] = useState('');

    // media-api uploading state
    const [uploadingFile, setUploadingFile] = useState(false);

    // at first mount, eventually get the data updates cached in local storage
    useEffect(() => {
      if (cachedUpdatesKey) {
        const localStorageCommentsStr = localStorage.getItem(
          `${cachedUpdatesKey}_comments`
        );
        if (localStorageCommentsStr) {
          setComments(localStorageCommentsStr);
        }
      }
    }, []);

    // If importedData is defined, the user will be asked for confirmation before leaving the page or changing route.
    // If impotedData is cleared (= null), the user will not be asked for confirmation.
    useEffect(() => {
      setAskBeforeLeaving(!!importedData);
    }, [importedData]);

    const {
      importedDataKey = 'rows',
      addComments,
      commentsKey = 'comments',
      addFile,
      fileKey = 'file',
      beforeDataHooks = [],
      parsing = {},
      mapping,
      aggregation,
      table
    } = config;
    const { encoding = 'utf8', csvParser, xmlParser } = parsing;

    // cache a static table config which is:
    // 'table' config provided in the props
    // or a table config built from the mapping config if provided
    // or a default one for empty tables if no mapping config provided
    const staticTableConfig = useMemo(
      () =>
        table ||
        (mapping
          ? buildTableConfigFromMapping(mapping)
          : importTableCommonConfig),
      [mapping, table]
    );

    // read the current value of imported data
    const {
      data = emptyTableData,
      tableConfig = staticTableConfig,
      file
    } = importedData || {};

    const clearImportedData = useCallback(() => {
      setImportedData(null);
    }, []);

    // handle the completion of csv parsing
    const completeDataImport = useCallback(
      (importFile, importResult) => {
        const { data: importData, foundColumns } = importResult;
        const importDataArray = Array.isArray(importData)
          ? importData
          : [importData];

        // final build of the table config
        // if we received foundColumns from the csv-parser it means that no mapping was provided and we need to resolve the table config at each csv upload
        // otherwise it means we can rely on the staticTableConfig built as a memo at the component first mount
        const importTableConfig = foundColumns
          ? buildTableConfigFromColumnNames(foundColumns)
          : staticTableConfig;

        // build the received data structure and send it
        const receivedData = {
          data: importDataArray,
          tableConfig: importTableConfig,
          file: importFile
        };

        setError(null);
        setImportState({});
        setImportedData(receivedData);
      },
      [staticTableConfig]
    );

    // run aggregation on the csv import result
    const runDataAggregation = useCallback(
      (importFile, parsingResult) => {
        // now run aggregation if specified
        const { data: parsingData } = parsingResult;
        runAggregation({
          inputData: parsingData,
          aggregationConfig: aggregation,
          workerReportingConfig: {
            reportStep: 0.01 // report every %
          },
          onSuccess: aggregationResult => {
            completeDataImport(importFile, aggregationResult);
          },
          onError: message => {
            setError({
              fileName: importFile ? importFile.name : 'Data',
              message: 'does not match the expected data structure',
              additionalInfo: message
            });
            setImportState({});
          },
          onReport: (status, step, progress) => {
            // set the state to aggregating and report the progress
            setImportState({
              status,
              step,
              progress: 0.5 * (1 + progress),
              file: importFile
            });
          }
        });
      },
      [aggregation, completeDataImport]
    );

    // handle upload of a new file
    const handleNewFile = useCallback(
      acceptedFiles => {
        if (!acceptedFiles || acceptedFiles.length === 0) return;
        setDropZoneActive(false);
        // call the csv parsing now
        const inputFile = acceptedFiles[0];
        if (inputFile.type === 'text/xml' && xmlParser) {
          runXMLParser(inputFile, xmlParser)
            .then(parsingResult => {
              if (aggregation) {
                // now run aggregation if specified
                runDataAggregation(inputFile, parsingResult);
                return;
              }
              completeDataImport(inputFile, parsingResult);
            })
            .catch(({ message, cause }) => {
              setError({
                fileName: inputFile ? inputFile.name : 'Data',
                message:
                  cause === 'notValidXML'
                    ? 'is not a valid xml'
                    : 'does not match the expected data structure',
                additionalInfo: message
              });
            });
        } else if (inputFile.type === 'text/csv') {
          runCsvParser({
            csvInput: inputFile,
            csvParserConfig: csvParser,
            mappingConfig: mapping,
            beforeDataHooks,
            fileEncoding: encoding,
            workerReportingConfig: {
              reportStep: 0.01 // report every %
            },
            onSuccess: parsingResult => {
              if (aggregation) {
                // now run aggregation if specified
                runDataAggregation(inputFile, parsingResult);
                return;
              }
              completeDataImport(inputFile, parsingResult);
            },
            onError: message => {
              setError({
                fileName: inputFile ? inputFile.name : 'Data',
                message: 'does not match the expected data structure',
                additionalInfo: message
              });
              setImportState({});
            },
            onReport: (status, step, progress) => {
              // set the state to parsing and report the progress
              setImportState({
                status,
                step,
                progress: (aggregation ? 0.5 : 1) * progress,
                file: inputFile
              });
            }
          });
        } else if (
          inputFile.type === 'application/vnd.ms-excel' ||
          inputFile.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
          readXls({ inputFile, setWorkbook })
            .then(xlsFile => {
              runXlsParser({
                xlsFile,
                mappingConfig: mapping,
                onSuccess: parsingResult => {
                  if (aggregation) {
                    // now run aggregation if specified
                    runDataAggregation(inputFile, parsingResult);
                    return;
                  }
                  completeDataImport(inputFile, parsingResult);
                },
                onError: message => {
                  setError({
                    fileName: inputFile ? inputFile.name : 'Data',
                    message: 'does not match the expected data structure',
                    additionalInfo: message
                  });
                  setImportState({});
                }
              });
            })
            .catch(err => {
              const { message, cause } = err;
              if (cause.type === 'dataNotReadable') {
                setError({
                  fileName: inputFile.name,
                  message
                });
              }

              if (cause.type === 'multiplesSheets') {
                setWorkbook(cause.workbook);
                setSheets(cause.workbook.SheetNames);
                setFile(inputFile);
                setShowSheetsModal(true);
              }
            });
        } else {
          setError({
            fileName: inputFile.name,
            message: `file's format (${inputFile.type}) is not valid`
          });
        }
      },
      [
        csvParser,
        xmlParser,
        mapping,
        encoding,
        aggregation,
        completeDataImport,
        workbook
      ]
    );

    const handleXlsFile = (inputFile, sheet) => {
      const xlsFile = sheetToJson(sheet, workbook);

      runXlsParser({
        xlsFile,
        mappingConfig: mapping,
        onSuccess: parsingResult => {
          if (aggregation) {
            // now run aggregation if specified
            runDataAggregation(inputFile, parsingResult);
            return;
          }
          completeDataImport(inputFile, parsingResult);
        },
        onError: message => {
          setError({
            fileName: inputFile.name,
            message: 'does not match the expected data structure',
            additionalInfo: message
          });
          setImportState({});
        }
      });
    };

    // set unset active drop zone
    const onDropZoneEnter = useCallback(() => {
      setDropZoneActive(true);
    }, []);
    const onDropZoneLeave = useCallback(() => {
      setDropZoneActive(false);
    }, []);

    // handle comments state
    const focusComments = useCallback(() => {
      setCommentsInputFocused(true);
    }, []);
    const blurComments = useCallback(() => {
      setCommentsInputFocused(false);
    }, []);
    const updateComments = useCallback(
      e => {
        const newComment = e.target.value;
        setComments(newComment);
        if (cachedUpdatesKey) {
          if (!newComment) {
            localStorage.removeItem(`${cachedUpdatesKey}_comments`);
            return;
          }
          localStorage.setItem(`${cachedUpdatesKey}_comments`, newComment);
        }
      },
      [cachedUpdatesKey]
    );

    // this memo is passed to the table as the 'update' context
    // it only lets the table cache user display config if there is a predefined mapping or a table config provided
    /**
     * @type {import('@/components/ui/table/table.type').TableUpdate}
     */
    const tableUpdate = useMemo(
      () => ({
        userDisplay: {
          localStorageKey:
            mapping || table ? `${cachedUpdatesKey}_tableConfig` : undefined
        }
      }),
      [cachedUpdatesKey, mapping, table]
    );

    // if submit is successful, dataImporter will cleanup all related localStorage items
    const cleanupLocalStorage = useCallback(() => {
      if (cachedUpdatesKey) {
        localStorage.removeItem(`${cachedUpdatesKey}_comments`);
        localStorage.removeItem(`${cachedUpdatesKey}_tableConfig`);
      }
    }, [cachedUpdatesKey]);

    // Update leaving alert callback
    useEffect(() => {
      setLeavingCallback(cleanupLocalStorage);
    }, [setLeavingCallback, cleanupLocalStorage]);

    // submit the data
    const submitData = useCallback(() => {
      const formData = {
        [importedDataKey]: data,
        [commentsKey]: comments ? comments : undefined
      };
      // check if we should add the file to the form data (and hence to media)
      if (addFile) {
        if (!file) {
          const errMessage = 'No file attached';
          console.error(errMessage);
          notify.error(errMessage);
          return;
        }
        setUploadingFile(true);
        uploadFile(
          file,
          savedFile => {
            formData[fileKey] = savedFile;
            setUploadingFile(false);
            onSubmit(formData, cleanupLocalStorage);
          },
          () => {
            setUploadingFile(false);
            handleUploadFileOnError(file.name, file.response.status);
          }
        );
        return;
      }
      onSubmit(formData, cleanupLocalStorage);
    }, [data, comments, file, config, onSubmit, cleanupLocalStorage]);

    // Xls sheets modal
    const handleSheetSelect = () => {
      handleXlsFile(fileData, selectedSheet);
      setShowSheetsModal(false);
    };

    const { MAX_FILE_SIZE } = uploadConfig;

    return (
      <>
        {/* If no valid imported data show the drop zone */}
        {!importedData && (
          <div className="absolute top-0 z-10 flex size-full flex-none flex-col items-center justify-center bg-black/10 backdrop-blur-[1px]">
            {importState.status === DATA_PARSING_STATUS_PARSING ||
            importState.status === DATA_PARSING_STATUS_AGGREGATING ? (
              <div className="flex w-[400px] flex-col flex-nowrap items-center rounded-sm bg-card p-5 text-sm">
                <div className="flex flex-row flex-nowrap items-center">
                  {`${importState.step}`}
                  <div className="mr-4 ml-1 font-bold">
                    {`${importState.file.name} (${filesize(
                      importState.file.size,
                      { round: 0 }
                    )})`}
                  </div>
                </div>
                <div className="mt-2 mb-1 h-2.5 w-full rounded-md border border-border">
                  <div
                    className="h-full rounded-lg bg-primary"
                    style={{ width: `${importState.progress * 100}%` }}
                  />
                </div>
                <div>{`${Math.floor(importState.progress * 100)}%`}</div>
              </div>
            ) : (
              <div className="min-w-[400px]">
                <DropZone
                  dropZoneActive={dropZoneActive}
                  error={error}
                  maxFileSize={MAX_FILE_SIZE}
                  onDrop={handleNewFile}
                  onDropZoneEnter={onDropZoneEnter}
                  onDropZoneLeave={onDropZoneLeave}
                  title={
                    xmlParser
                      ? 'Drag an XML, CSV or Excel file here'
                      : 'Drag a CSV or Excel file here'
                  }
                />
              </div>
            )}
          </div>
        )}
        <div className="relative h-[calc(100%-var(--spacing-full-navbar))]">
          <div
            className={cn(
              'absolute top-0 flex size-full flex-col flex-nowrap items-center',
              !importedData && 'opacity-100'
            )}
          >
            {importedData && (
              <div className="m-5 mb-0 flex flex-row flex-nowrap items-center rounded-lg border border-border bg-card p-3 text-sm">
                <p>
                  <span data-cy="data-importer-nb-imports">
                    {formatNumber(data.length)}
                  </span>{' '}
                  records have been successfully imported from
                </p>
                <p className="mr-4 ml-1 font-bold">
                  <span data-cy="data-importer-file-name">{file.name}</span>{' '}
                  <span>({filesize(file.size, { round: 0 })})</span>
                </p>
                <Button
                  data-cy="retry-import-button"
                  onClick={clearImportedData}
                  type="button"
                  variant="outlinePrimary"
                >
                  Try again
                </Button>
              </div>
            )}
            <div className="flex w-full grow flex-col flex-nowrap p-5 pb-0">
              <Table config={tableConfig} data={data} update={tableUpdate} />
            </div>
          </div>
        </div>
        <div className="z-2 flex w-full shrink-0 flex-row flex-nowrap items-end justify-between gap-5 border-border border-t bg-card px-5 py-3 text-sm">
          <div className="flex min-h-[40px] flex-row flex-nowrap items-center gap-4">
            <Button
              aria-label="Cancel import action"
              data-cy="cancel"
              disabled={false}
              onClick={onCancel}
              variant="outline"
            >
              {cancelLabel}
            </Button>
            {traceInfoViewer && (
              <Button
                active={traceInfoViewer.showTraceInfoTray}
                data-cy="toggle-trace-info"
                onClick={traceInfoViewer.toggleShowTraceInfoTray}
                variant="pushtext"
              >
                <List size={20} /> Show Trace Info
              </Button>
            )}
          </div>
          {addComments && (
            <div className="relative w-full max-w-[500px]">
              {/* <div className="absolute bottom-0 h-auto w-full grow"></div> */}
              <Textarea
                className={cn(
                  'h-[5rem] max-h-[250px] min-h-[2.5rem] bg-card transition-all duration-100 ease-out',
                  !commentsInputFocused && 'h-[2.5rem]'
                )}
                data-cy="importer-comment"
                onBlur={blurComments}
                onChange={updateComments}
                onFocus={focusComments}
                placeholder="Add a comment"
              />
            </div>
          )}
          <Button
            data-cy="submit-button"
            disabled={
              !(importedData && importedData.data.length) ||
              submitDisabled ||
              uploadingFile
            }
            onClick={submitData}
          >
            {submitLabel}
          </Button>
        </div>

        <Dialog onOpenChange={setShowSheetsModal} open={showSheetsModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Which sheet do you want to import?</DialogTitle>
              <DialogDescription>
                We found multiple sheets in the file you&apos;ve uploaded.
                Please select the one you want to import.
              </DialogDescription>
            </DialogHeader>
            <RadioGroup
              defaultValue="comfortable"
              onValueChange={setSelectedSheet}
            >
              {sheets.map(sheet => (
                <div
                  className="flex items-center"
                  data-cy={`select-${sheet}`}
                  key={sheet}
                >
                  <RadioGroupItem id={sheet} value={sheet} />
                  <Label className="pl-3" htmlFor={sheet}>
                    {sheet}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            <DialogFooter>
              <Button
                data-cy="cancel-sheet-btn"
                onClick={() => setShowSheetsModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                data-cy="confirm-sheet-btn"
                disabled={!selectedSheet}
                onClick={handleSheetSelect}
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
);
DataImporter.propTypes = {
  config: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
  submitDisabled: PropTypes.bool,
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.string,
  cachedUpdatesKey: PropTypes.string,
  traceInfoViewer: PropTypes.shape({
    showTraceInfoTray: PropTypes.bool.isRequired,
    toggleShowTraceInfoTray: PropTypes.func.isRequired
  })
};
